/**
 * FlowHub - Performance Monitoring Hook
 * Tracks Web Vitals and user experience metrics
 */

import { useEffect } from 'react';
import { logger } from '../lib/logger';

interface WebVitalsMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
}

/**
 * Hook to monitor Core Web Vitals
 * - LCP (Largest Contentful Paint)
 * - FID (First Input Delay)
 * - CLS (Cumulative Layout Shift)
 * - FCP (First Contentful Paint)
 * - TTFB (Time to First Byte)
 */
export function usePerformanceMonitoring() {
  useEffect(() => {
    if (import.meta.env.PROD && 'PerformanceObserver' in window) {
      // Track Largest Contentful Paint (LCP)
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as any;
        const lcp = lastEntry.renderTime || lastEntry.loadTime;
        
        const rating = lcp <= 2500 ? 'good' : lcp <= 4000 ? 'needs-improvement' : 'poor';
        
        if (rating !== 'good') {
          logger.warn('LCP needs improvement', { lcp, rating });
        }
        
        reportMetric({ name: 'LCP', value: lcp, rating, delta: lcp });
      });
      
      try {
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        // Silently fail if not supported
      }

      // Track First Input Delay (FID)
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          const fid = entry.processingStart - entry.startTime;
          const rating = fid <= 100 ? 'good' : fid <= 300 ? 'needs-improvement' : 'poor';
          
          if (rating !== 'good') {
            logger.warn('FID needs improvement', { fid, rating });
          }
          
          reportMetric({ name: 'FID', value: fid, rating, delta: fid });
        });
      });
      
      try {
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        // Silently fail if not supported
      }

      // Track Cumulative Layout Shift (CLS)
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
      });
      
      try {
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // Silently fail if not supported
      }

      // Report CLS on page hide
      const reportCLS = () => {
        const rating = clsValue <= 0.1 ? 'good' : clsValue <= 0.25 ? 'needs-improvement' : 'poor';
        
        if (rating !== 'good') {
          logger.warn('CLS needs improvement', { cls: clsValue, rating });
        }
        
        reportMetric({ name: 'CLS', value: clsValue, rating, delta: clsValue });
      };

      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          reportCLS();
        }
      });

      // Cleanup
      return () => {
        lcpObserver.disconnect();
        fidObserver.disconnect();
        clsObserver.disconnect();
      };
    }
  }, []);
}

/**
 * Report metric to analytics/monitoring service
 */
function reportMetric(metric: WebVitalsMetric) {
  // TODO: Send to analytics service (Google Analytics, Vercel Analytics, etc.)
  // Example:
  // if (window.gtag) {
  //   window.gtag('event', metric.name, {
  //     value: Math.round(metric.value),
  //     metric_rating: metric.rating,
  //   });
  // }
  
  // For now, store in sessionStorage
  try {
    const metrics = JSON.parse(sessionStorage.getItem('web_vitals') || '[]');
    metrics.push({
      ...metric,
      timestamp: new Date().toISOString(),
      url: window.location.pathname,
    });
    sessionStorage.setItem('web_vitals', JSON.stringify(metrics.slice(-20)));
  } catch (e) {
    // Silently fail
  }
}
